import { Component } from '@theme/component';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const VISIBILITY_THRESHOLD = 0.6;

/**
 * Wraps a single native Shopify product-media <deferred-media> video slide inside the
 * PDP gallery (product-media-gallery-content.liquid, via product-media.liquid). Only ever
 * rendered around a regular gallery media item whose media_type is 'video' -- render 'video'
 * only wraps its output in this element when gallery_video_autoplay: true is passed, which
 * product-media.liquid sets for exactly that case and never for the dvs.showcase_video slide
 * or for 'external_video' (YouTube/Vimeo) media. Kept fully independent of
 * dvs-showcase-video.js/<dvs-showcase-video> by design, even though the visibility/active-slide
 * logic below is intentionally identical -- neither file imports or extends the other, so a
 * future change to one cannot regress the other.
 *
 * Autoplays (muted, looped, playsinline) only while its slide is both the active slideshow
 * slide -- tracked via slideshow.js's own aria-hidden attribute on the ancestor
 * <slideshow-slide>, no changes to slideshow.js required -- and actually visible in the
 * viewport. In contexts with no single active slide (desktop grid tiles, the zoom dialog's
 * full-size list), there is no ancestor <slideshow-slide>, so "active" is always true and only
 * viewport visibility gates playback.
 *
 * Reuses deferred-media's existing loadContent()/pauseMedia() and the theme's stock
 * poster-fade CSS (.deferred-media__playing) -- no new visual chrome. deferred-media's own
 * ThemeEvents.mediaStartedPlaying listener (assets/media.js) already pauses every other loaded
 * deferred-media on the page when one starts loading, so hidden/other gallery videos are never
 * left playing simultaneously -- no extra cross-video coordination needed here.
 *
 * @extends Component
 */
export class DvsGalleryVideo extends Component {
  /** @type {Element | null} */
  #slideEl = null;
  /** @type {MutationObserver | null} */
  #slideObserver = null;
  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;
  #visible = false;
  #active = true;
  #reducedMotionQuery = window.matchMedia(REDUCED_MOTION_QUERY);
  #onReducedMotionChange = () => this.#sync();

  connectedCallback() {
    super.connectedCallback();

    this.#slideEl = this.closest('slideshow-slide');
    if (this.#slideEl) {
      this.#active = this.#slideEl.getAttribute('aria-hidden') !== 'true';
      this.#slideObserver = new MutationObserver(() => {
        const active = this.#slideEl?.getAttribute('aria-hidden') !== 'true';
        if (active !== this.#active) {
          this.#active = active;
          this.#sync();
        }
      });
      this.#slideObserver.observe(this.#slideEl, { attributes: true, attributeFilter: ['aria-hidden'] });
    }

    this.#intersectionObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries.at(-1)?.isIntersecting ?? false;
        if (visible !== this.#visible) {
          this.#visible = visible;
          this.#sync();
        }
      },
      { threshold: VISIBILITY_THRESHOLD }
    );
    this.#intersectionObserver.observe(this);

    this.#reducedMotionQuery.addEventListener('change', this.#onReducedMotionChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersectionObserver?.disconnect();
    this.#slideObserver?.disconnect();
    this.#reducedMotionQuery.removeEventListener('change', this.#onReducedMotionChange);
  }

  #sync() {
    const deferredMedia = this.querySelector('deferred-media');
    if (!deferredMedia) return;

    const shouldPlay = this.#visible && this.#active && !this.#reducedMotionQuery.matches;
    const existingVideo = deferredMedia.querySelector('video');

    if (!shouldPlay) {
      // Leave the slide: pause and reset so it starts fresh next time it becomes active.
      if (existingVideo) {
        existingVideo.pause();
        existingVideo.currentTime = 0;
      }
      return;
    }

    if (!deferredMedia.hasAttribute('data-media-loaded')) {
      // First time this slide becomes active + visible: swap the poster for the real
      // video, same as a manual click would (loadContent is deferred-media's own API).
      deferredMedia.loadContent(false);
    }

    const video = deferredMedia.querySelector('video');
    if (!video) return;

    // Force the IDL properties (not just the HTML attributes video_tag already renders)
    // -- required for Safari to resolve play() without a user gesture.
    video.muted = true;
    video.defaultMuted = true;
    video.loop = true;
    video.playsInline = true;

    const playAttempt = video.play();
    if (playAttempt && typeof playAttempt.catch === 'function') {
      playAttempt.catch(() => {
        // Autoplay blocked by the browser: the poster and the existing manual
        // play/pause control (rendered via disable_controls) remain the fallback.
      });
    }
  }
}

if (!customElements.get('dvs-gallery-video')) {
  customElements.define('dvs-gallery-video', DvsGalleryVideo);
}
